// /privacy — the privacy policy. Public and bare (see chrome.ts's BARE_PATHS
// and main.ts's PUBLIC_PATHS): reachable with no session, since a carrier
// vetting an A2P 10DLC campaign and a signed-out visitor both need to read it.

import { type View } from '../router.js';
import { el } from '../dom.js';
import { authBrand } from './authBrand.js';

export const privacyView: View = (outlet) => {
  outlet.append(
    el('main', { class: 'page' }, [
      authBrand(),
      el('h1', {}, 'Privacy Policy'),
      el('p', {}, 'Last updated: October 15, 2026'),

      el('h2', {}, 'What we collect'),
      el('p', {}, [
        'To create an account, we collect your name and either an email ',
        'address or a phone number (whichever you register with). If you add ',
        'a phone number to an existing account, we verify it by sending a ',
        'one-time code by text message before we store it.',
      ]),
      el('p', {}, [
        'Using MySocialCal may also involve: photos you upload to a group, ',
        'the groups and events you create or join, RSVP and day-of ',
        'confirmation responses, chat messages within an event, and your ',
        'notification preferences. If you enable push notifications, we ',
        'store the device or browser subscription needed to deliver them.',
      ]),
      el('p', {}, [
        'We keep a security log of account activity (such as sign-ins and ',
        'password changes) and, for abuse prevention, the IP address a ',
        'request came from.',
      ]),

      // The address book, added 2026-09-08. It was the biggest gap in this
      // page: a user stores the personal data of people who never signed up,
      // and the App Store nutrition label declares both Contacts and Physical
      // Address. A label that declares data the policy does not mention is the
      // mismatch a reviewer asks about.
      el('h2', {}, 'Your address book'),
      el('p', {}, [
        'MySocialCal includes a personal address book. Anything you save there ',
        'is data you entered or imported yourself, and it can include other ',
        "people's names, phone numbers, email addresses and postal addresses. ",
        'Those people are not MySocialCal users unless they sign up ',
        'themselves, and we never contact them because you saved them.',
      ]),
      el('p', {}, [
        'Your address book is private to your account. Other members of your ',
        'groups cannot see it, and it is never used to suggest people to ',
        'anyone else. The app does not read the contact list on your phone; ',
        'entries exist only because you added or imported them.',
      ]),
      el('p', {}, [
        'You are responsible for the information you save about other people. ',
        'You can edit or delete any entry at any time, and deleting your ',
        'account deletes your whole address book with it.',
      ]),

      // Reports, added 2026-09-08 with the report control (Guideline 1.2).
      el('h2', {}, 'Reporting content'),
      el('p', {}, [
        'If you report a post, chat message or photo, we store who reported ',
        'it, who wrote it, the reason you chose, any note you added, and a ',
        'copy of the reported content as it was at that moment. We keep the ',
        'copy so a report stays reviewable even if the content is deleted ',
        'afterwards.',
      ]),
      el('p', {}, [
        'Reports are visible only to us, not to the person you reported. We ',
        'use them to review content and to act on accounts that break the ',
        'rules, and for nothing else.',
      ]),

      el('h2', {}, 'How we use it'),
      el('p', {}, [
        'Your information is used to operate the service you signed up for: ',
        'running your groups and events, keeping your address book, sending ',
        'the notifications you have opted into (email and/or SMS), reviewing ',
        'reported content, and keeping your account secure. We do not use ',
        'your data for advertising, and MySocialCal does not run any ',
        'third-party analytics or tracking scripts.',
      ]),

      el('h2', {}, 'Text messages (SMS)'),
      el('p', {}, [
        'If you provide a phone number, you may receive SMS for: a ',
        'verification code when you add or confirm a number, invitations to ',
        'a group, and the notifications you choose to receive by text: event ',
        'announcements, day-of "still coming?" prompts, headcount and RSVP ',
        'summaries for events you organize, notices that an event is on or ',
        'full, notices of new posts and event chat messages in your groups, ',
        'and join requests for groups you manage. Message and data rates may apply. ',
        'Reply STOP to any message to stop receiving texts, or HELP for ',
        'help. We will never sell or share your mobile number with third ',
        'parties or affiliates for their marketing purposes.',
      ]),

      el('h2', {}, 'Who we share it with'),
      el('p', {}, [
        'We do not sell your personal information. We share data only with ',
        'the vendors that operate the service on our behalf (sending email, ',
        'sending SMS, and hosting uploaded photos) solely to deliver those ',
        'features, and with other members of a group you belong to, to the ',
        'extent that group membership, events, RSVPs, photos, and chat are ',
        'inherently visible to that group.',
      ]),

      el('h2', {}, 'Data retention and deletion'),
      el('p', {}, [
        'We keep your data for as long as your account is active. You can ',
        'delete your account yourself at any time, from ',
        el('a', { href: '/account' }, 'your account page'),
        '. You do not have to ask us, and you do not have to email anybody.',
      ]),
      el('p', {}, [
        'Deleting your account signs you out on every device and removes your ',
        'email address, phone number, postal address and profile photo. You ',
        'leave every group, and any group where you were the only member is ',
        'deleted with you. The email address becomes free to register again.',
      ]),
      // Named field by field on purpose. Item 1 of docs/plan-ios-ship.md
      // requires that whatever a deleted account keeps is documented here,
      // because App Store review reads this page -- and because a user is owed
      // the same answer.
      el('p', {}, [
        'Three things stay. Your first and last name remain on the posts, ',
        'events, results and chat messages other members can still see, so ',
        'their own history stays readable rather than turning into a row of ',
        'anonymous entries. We keep an internal record that the account ',
        'existed and was closed, which is what stops the same account being ',
        'silently recreated or reopened.',
      ]),
      // Added 2026-09-09, with the change that stopped scrubbing reg_email and
      // introduced reg_phone. Retaining an identifier through a deletion request
      // is exactly the kind of thing this page exists to state plainly, and App
      // Store review reads it. Say what is kept, why, and that it grants no
      // access -- neither column is read by findAccountByIdentifier().
      el('p', {}, [
        'And we keep the email address and phone number the account was ',
        'originally registered with, for one narrow reason: to recognise ',
        'someone who abuses the service, deletes the account, and signs up ',
        'again. They are kept as a record only. Neither can be used to sign ',
        'in, neither is shown to any other member, and keeping them does not ',
        'stop that email address being registered again by you or anyone ',
        'else. Nothing else about you is kept.',
      ]),
      el('p', {}, [
        'If you own a group that has other members in it, we ask you to hand ',
        'it to one of them before you delete your account. Otherwise the ',
        'group would be left with nobody able to manage it.',
      ]),

      el('h2', {}, 'Contact us'),
      el('p', {}, ['Questions about this policy: ', el('a', { href: '/contact' }, 'contact us'), '.']),

      el('p', { class: 'signed-out-only' }, [el('a', { href: '/login' }, 'Back to log in')]),
    ]),
  );
};
